import { EquipmentCategory, type EquipmentKey, isEquipmentKey } from "./training";

/*
 * One illustration per equipment item, drawn on a 64×64 grid standing on a floor at y=54.
 * Three paints, filled in from the app's theme by `equipmentArtSvg`:
 * - INK: upholstery, grips and the parts you touch;
 * - STEEL: frames, rails and bars;
 * - ACCENT: the load - plates, weight stacks, flywheels - so every piece reads "this is the weight".
 */

/** x, y, width, height */
type Box = [number, number, number, number];

const shadow = (rx = 24) => `<ellipse cx="32" cy="55.5" rx="${rx}" ry="2" fill="INK" fill-opacity="0.09"/>`;
const line = (d: string, paint: string, width: number) =>
  `<path d="${d}" fill="none" stroke="${paint}" stroke-width="${width}" stroke-linecap="round" stroke-linejoin="round"/>`;
const rect = ([x, y, w, h]: Box, paint: string, r = 1) =>
  `<rect x="${x}" y="${y}" width="${w}" height="${h}" rx="${r}" fill="${paint}"/>`;
const circle = ([cx, cy, r]: [number, number, number], paint: string) =>
  `<circle cx="${cx}" cy="${cy}" r="${r}" fill="${paint}"/>`;

/** A selectorised weight stack: `count` plates with a hairline gap and the guide rod through them. */
const stack = ([x, y, w, h]: Box, count: number) => {
  const plate = (h - (count - 1)) / count;
  const plates = Array.from({ length: count }, (_, index) =>
    rect([x, y + index * (plate + 1), w, plate], "ACCENT", 0.75),
  );
  return `${plates.join("")}${rect([x + w / 2 - 0.75, y - 4, 1.5, h + 4], "STEEL", 0.75)}`;
};

/** A stack in its tower: two posts, a cap and the plates between them. */
const tower = (x: number, top: number, w = 12) =>
  `${rect([x, top, 2, 54 - top], "STEEL")}${rect([x + w + 2, top, 2, 54 - top], "STEEL")}${rect([x, top, w + 4, 2.5], "STEEL")}${stack([x + 2.5, 54 - 24, w - 1, 23], 7)}`;

const EQUIPMENT_ART: Record<EquipmentKey, string> = {
  dumbbells: [
    shadow(22),
    // the back one, smaller and in steel, gives the pair depth
    rect([37, 33.25, 14, 2.5], "STEEL", 1.25),
    rect([30, 26, 7.5, 16], "STEEL", 2),
    rect([50.5, 26, 7.5, 16], "STEEL", 2),
    rect([7, 33, 11, 21], "ACCENT", 2.5),
    rect([32, 33, 11, 21], "ACCENT", 2.5),
    rect([18, 39, 2, 9], "INK", 0.75),
    rect([30, 39, 2, 9], "INK", 0.75),
    rect([20, 42, 10, 3], "INK", 1.5),
  ].join(""),

  kettlebells: [
    shadow(18),
    line("M21.5 34 C19 16 45 16 42.5 34", "INK", 4.5),
    `<path d="M32 26 C42.5 26 48 33 48 41 C48 47.5 45 52 41.5 54 H22.5 C19 52 16 47.5 16 41 C16 33 21.5 26 32 26 Z" fill="ACCENT"/>`,
    `<path d="M22 37 C23.5 32.5 27 30.5 31 30" fill="none" stroke="INK" stroke-opacity="0.18" stroke-width="2.5" stroke-linecap="round"/>`,
    rect([26, 51.5, 12, 2.5], "INK", 1),
  ].join(""),

  barbell: [
    shadow(26),
    rect([4, 36.75, 56, 2.5], "STEEL", 1.25),
    rect([4, 36, 7, 4], "STEEL", 1),
    rect([53, 36, 7, 4], "STEEL", 1),
    rect([10, 22, 4.5, 32], "ACCENT", 1.5),
    rect([15.5, 27, 3.5, 22], "ACCENT", 1.25),
    rect([49.5, 22, 4.5, 32], "ACCENT", 1.5),
    rect([45, 27, 3.5, 22], "ACCENT", 1.25),
    rect([19.5, 34, 2, 8], "INK", 0.75),
    rect([42.5, 34, 2, 8], "INK", 0.75),
    rect([26, 36.75, 12, 2.5], "INK", 1.25),
  ].join(""),

  ez_bar: [
    shadow(24),
    line("M6 40 H19 L22.5 36 L26.5 43 L30 38 H34 L37.5 43 L41.5 36 L45 40 H58", "STEEL", 2.5),
    rect([9, 26, 5, 28], "ACCENT", 1.5),
    rect([50, 26, 5, 28], "ACCENT", 1.5),
    rect([14.5, 36.5, 2, 7], "INK", 0.75),
    rect([47.5, 36.5, 2, 7], "INK", 0.75),
  ].join(""),

  trap_bar: [
    shadow(26),
    rect([4, 37, 12, 3], "STEEL", 1.5),
    rect([48, 37, 12, 3], "STEEL", 1.5),
    line("M24 24 H40 L49 38.5 L40 53 H24 L15 38.5 Z", "STEEL", 3),
    rect([6, 25, 4.5, 27], "ACCENT", 1.5),
    rect([53.5, 25, 4.5, 27], "ACCENT", 1.5),
    rect([21, 32, 3.5, 13], "INK", 1.75),
    rect([39.5, 32, 3.5, 13], "INK", 1.75),
  ].join(""),

  plates: [
    shadow(24),
    circle([45, 42, 12], "STEEL"),
    circle([45, 42, 2.5], "INK"),
    circle([27, 36, 18], "ACCENT"),
    `<circle cx="27" cy="36" r="13" fill="none" stroke="INK" stroke-opacity="0.16" stroke-width="1.5"/>`,
    circle([27, 36, 5.5], "STEEL"),
    circle([27, 36, 3], "INK"),
  ].join(""),

  flat_bench: [
    shadow(25),
    rect([13, 36, 3, 17], "STEEL", 1),
    rect([48, 36, 3, 17], "STEEL", 1),
    rect([15, 40, 34, 2.5], "STEEL", 1),
    rect([8, 51.5, 13, 3], "STEEL", 1.5),
    rect([43, 51.5, 13, 3], "STEEL", 1.5),
    rect([7, 29, 50, 7], "INK", 3),
  ].join(""),

  adjustable_bench: [
    shadow(25),
    line("M10 52.5 H55", "STEEL", 3),
    line("M30 52.5 V40", "STEEL", 3),
    line("M50 52.5 V41", "STEEL", 3),
    line("M38 52.5 L22.5 30", "STEEL", 2.5),
    line("M29 36.5 L12.5 15", "INK", 7),
    line("M33 38 H53", "INK", 7),
  ].join(""),

  squat_rack: [
    shadow(26),
    rect([14, 10, 3, 43], "STEEL", 1),
    rect([47, 10, 3, 43], "STEEL", 1),
    rect([14, 9, 36, 3], "STEEL", 1),
    rect([9, 51.5, 13, 3], "STEEL", 1.5),
    rect([42, 51.5, 13, 3], "STEEL", 1.5),
    rect([11, 40, 9, 2], "INK", 1),
    rect([44, 40, 9, 2], "INK", 1),
    rect([17, 26.5, 2.5, 4], "INK", 0.75),
    rect([44.5, 26.5, 2.5, 4], "INK", 0.75),
    rect([4, 24.75, 56, 2.5], "STEEL", 1.25),
    rect([6, 15, 4, 22], "ACCENT", 1.5),
    rect([54, 15, 4, 22], "ACCENT", 1.5),
  ].join(""),

  smith_machine: [
    shadow(26),
    rect([10, 8, 3, 45], "STEEL", 1),
    rect([51, 8, 3, 45], "STEEL", 1),
    rect([10, 7, 44, 3], "STEEL", 1),
    rect([20, 10, 1.5, 43], "STEEL", 0.75),
    rect([42.5, 10, 1.5, 43], "STEEL", 0.75),
    rect([7, 51.5, 50, 3], "STEEL", 1.5),
    rect([4, 31.75, 56, 2.5], "STEEL", 1.25),
    rect([18, 29, 5.5, 8], "INK", 1.25),
    rect([40.5, 29, 5.5, 8], "INK", 1.25),
    rect([5, 22, 4, 22], "ACCENT", 1.5),
    rect([55, 22, 4, 22], "ACCENT", 1.5),
  ].join(""),

  pull_up_bar: [
    shadow(24),
    rect([17, 16, 3, 37], "STEEL", 1),
    rect([44, 16, 3, 37], "STEEL", 1),
    rect([11, 51.5, 15, 3], "STEEL", 1.5),
    rect([38, 51.5, 15, 3], "STEEL", 1.5),
    rect([20, 36, 24, 2.5], "STEEL", 1),
    line("M5 21 L10 14 H54 L59 21", "INK", 3.5),
  ].join(""),

  dip_station: [
    shadow(24),
    line("M22 24 L26 21 M42 24 L38 21", "STEEL", 2.5),
    line("M18 53 V24", "STEEL", 3),
    line("M46 53 V24", "STEEL", 3),
    line("M18 43 H46", "STEEL", 2.5),
    line("M11 52.5 H25 M39 52.5 H53", "STEEL", 3),
    line("M18 25 L8 32", "INK", 4.5),
    line("M46 25 L56 32", "INK", 4.5),
  ].join(""),

  preacher_bench: [
    shadow(23),
    line("M14 52.5 H52", "STEEL", 3),
    line("M24 52.5 V34", "STEEL", 3),
    line("M44 52.5 V42", "STEEL", 3),
    line("M24 37 L33 42", "STEEL", 2.5),
    line("M10 22 L18 16", "STEEL", 2),
    rect([7, 19, 3, 6], "ACCENT", 1),
    line("M15 23 L27 36", "INK", 7),
    line("M36 40 H51", "INK", 6),
  ].join(""),

  cable_station: [
    shadow(26),
    tower(25, 8, 12),
    rect([8, 8, 2.5, 45], "STEEL", 1),
    rect([53.5, 8, 2.5, 45], "STEEL", 1),
    rect([8, 7, 48, 3], "STEEL", 1),
    rect([6, 51.5, 52, 3], "STEEL", 1.5),
    circle([13, 22, 2.5], "INK"),
    circle([51, 22, 2.5], "INK"),
    `<path d="M13 24.5 L17 36 M51 24.5 L47 36" stroke="INK" stroke-width="1" fill="none"/>`,
    line("M15 36.5 H19", "INK", 3),
    line("M45 36.5 H49", "INK", 3),
  ].join(""),

  lat_pulldown: [
    shadow(26),
    tower(43, 6, 12),
    rect([16, 6, 31, 3], "STEEL", 1),
    circle([19, 10.5, 2.5], "INK"),
    `<path d="M19 13 V21" stroke="INK" stroke-width="1" fill="none"/>`,
    line("M5 25 L8.5 21 H29.5 L33 25", "INK", 3),
    line("M24 52.5 V43", "STEEL", 3),
    line("M12 52.5 H34", "STEEL", 3),
    rect([13, 39, 22, 5], "INK", 2.5),
    rect([15, 32, 18, 4], "INK", 2),
    line("M34 34 H43", "STEEL", 2.5),
  ].join(""),

  seated_row: [
    shadow(26),
    tower(44, 14, 12),
    line("M6 46 H44", "STEEL", 3),
    line("M9 46 V53", "STEEL", 3),
    line("M24 46 V53", "STEEL", 3),
    rect([8, 38, 18, 5], "INK", 2.5),
    line("M37 45 L40.5 33", "INK", 4),
    circle([44, 44, 2.5], "INK"),
    `<path d="M42 43 L30 38" stroke="INK" stroke-width="1" fill="none"/>`,
    `<path d="M27 36 L31 38 L27 40" fill="none" stroke="INK" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round"/>`,
  ].join(""),

  leg_press: [
    shadow(27),
    line("M6 52.5 H58", "STEEL", 3),
    line("M24 52.5 L55 17", "STEEL", 3),
    line("M15 52.5 V44", "STEEL", 3),
    line("M6 35 L13 49", "INK", 7),
    line("M15 47.5 H26", "INK", 6),
    line("M34 27 L45 37", "INK", 4.5),
    line("M45.5 18.5 L55.5 27.5", "ACCENT", 7),
  ].join(""),

  hack_squat: [
    shadow(25),
    line("M8 52.5 H56", "STEEL", 3),
    line("M18 52.5 L42 8", "STEEL", 3),
    line("M44 52.5 V30", "STEEL", 3),
    line("M10 48 L32 44", "INK", 4),
    line("M27 36 L38 16", "INK", 7),
    rect([32.5, 10, 9, 4], "INK", 2),
    line("M42 30 L36 20", "STEEL", 2.5),
    rect([45, 18, 5, 18], "ACCENT", 1.5),
  ].join(""),

  chest_press_machine: [
    shadow(26),
    tower(43, 10, 12),
    line("M8 52.5 H44", "STEEL", 3),
    line("M20 52.5 V42", "STEEL", 3),
    rect([11, 15, 6, 27], "INK", 3),
    rect([11, 39, 18, 5], "INK", 2.5),
    line("M45 15 Q36 16 34 25", "STEEL", 2.5),
    line("M27 25.5 H36", "INK", 3.5),
    line("M34 21 V30", "INK", 3.5),
  ].join(""),

  shoulder_press_machine: [
    shadow(26),
    tower(43, 10, 12),
    line("M8 52.5 H44", "STEEL", 3),
    line("M20 52.5 V42", "STEEL", 3),
    rect([11, 17, 6, 25], "INK", 3),
    rect([11, 39, 18, 5], "INK", 2.5),
    line("M45 22 Q34 12 25 12", "STEEL", 2.5),
    line("M23 7 V17", "INK", 3.5),
  ].join(""),

  pec_deck: [
    shadow(25),
    stack([27, 4, 10, 9], 3),
    line("M10 52.5 H54", "STEEL", 3),
    line("M32 52.5 V43", "STEEL", 3),
    rect([27, 14, 10, 27], "INK", 3),
    rect([23, 39, 18, 5], "INK", 2.5),
    line("M32 13 Q20 8 15 15", "STEEL", 2.5),
    line("M32 13 Q44 8 49 15", "STEEL", 2.5),
    rect([11, 14, 6, 19], "INK", 3),
    rect([47, 14, 6, 19], "INK", 3),
  ].join(""),

  leg_extension: [
    shadow(26),
    tower(43, 14, 12),
    line("M6 52.5 H44", "STEEL", 3),
    line("M22 52.5 V42", "STEEL", 3),
    line("M12 18 L14.5 39", "INK", 7),
    line("M15 40.5 H33", "INK", 6),
    line("M35 41 L37 50", "STEEL", 2.5),
    circle([37.5, 50, 3.5], "INK"),
    line("M37 41 L43 33", "STEEL", 2),
  ].join(""),

  leg_curl: [
    shadow(26),
    tower(44, 26, 12),
    line("M6 52.5 H45", "STEEL", 3),
    line("M12 52.5 V40", "STEEL", 3),
    line("M32 52.5 V40", "STEEL", 3),
    line("M7 35 Q19 30 25 36 H38", "INK", 7),
    line("M38 37 L40 27", "STEEL", 2.5),
    circle([40, 25.5, 3.5], "INK"),
  ].join(""),

  hip_thrust_machine: [
    shadow(26),
    line("M6 52.5 H58", "STEEL", 3),
    line("M12 52.5 V40", "STEEL", 3),
    line("M7 36.5 H18", "INK", 7),
    line("M47 52.5 L36 33", "STEEL", 3),
    rect([22, 28, 18, 6], "INK", 3),
    line("M40 31 H48", "STEEL", 2.5),
    rect([48, 20, 5, 22], "ACCENT", 1.5),
  ].join(""),

  calf_raise_machine: [
    shadow(26),
    tower(43, 10, 12),
    rect([16, 46, 18, 7], "INK", 1.5),
    line("M14 53 H44", "STEEL", 3),
    line("M45 16 H24", "STEEL", 2.5),
    line("M20 18 H30", "INK", 5),
    line("M24 16 V22", "STEEL", 2.5),
  ].join(""),

  adductor_machine: [
    shadow(25),
    line("M12 52.5 H52", "STEEL", 3),
    line("M32 52.5 V42", "STEEL", 3),
    rect([26, 13, 12, 25], "INK", 3),
    rect([23, 36, 18, 5], "INK", 2.5),
    line("M30 42 L13 33", "STEEL", 2.5),
    line("M34 42 L51 33", "STEEL", 2.5),
    rect([9, 21, 6, 15], "INK", 3),
    rect([49, 21, 6, 15], "INK", 3),
    line("M17 16 L21 19 L17 22", "ACCENT", 2),
    line("M47 16 L43 19 L47 22", "ACCENT", 2),
  ].join(""),

  abductor_machine: [
    shadow(25),
    line("M12 52.5 H52", "STEEL", 3),
    line("M32 52.5 V42", "STEEL", 3),
    rect([26, 13, 12, 25], "INK", 3),
    rect([23, 36, 18, 5], "INK", 2.5),
    line("M30 42 L20 34", "STEEL", 2.5),
    line("M34 42 L44 34", "STEEL", 2.5),
    rect([16, 22, 6, 15], "INK", 3),
    rect([42, 22, 6, 15], "INK", 3),
    line("M14 16 L10 19 L14 22", "ACCENT", 2),
    line("M50 16 L54 19 L50 22", "ACCENT", 2),
  ].join(""),

  assisted_pull_up: [
    shadow(26),
    rect([10, 8, 3, 45], "STEEL", 1),
    rect([51, 8, 3, 45], "STEEL", 1),
    rect([10, 7, 44, 3], "STEEL", 1),
    rect([7, 51.5, 50, 3], "STEEL", 1.5),
    line("M5 15 L11 10 M59 15 L53 10", "INK", 3.5),
    stack([25, 38, 14, 15], 4),
    line("M13 30 H24", "STEEL", 2.5),
    rect([22, 28, 20, 5], "INK", 2.5),
    rect([13, 42, 7, 2.5], "INK", 1.25),
    rect([44, 42, 7, 2.5], "INK", 1.25),
  ].join(""),

  resistance_bands: [
    shadow(22),
    `<ellipse cx="36" cy="40" rx="19" ry="9" transform="rotate(14 36 40)" fill="none" stroke="STEEL" stroke-width="4"/>`,
    `<ellipse cx="28" cy="34" rx="19" ry="9" transform="rotate(-16 28 34)" fill="none" stroke="ACCENT" stroke-width="4.5"/>`,
  ].join(""),

  ab_wheel: [
    shadow(20),
    line("M12 42 H52", "STEEL", 2.5),
    rect([6, 39, 12, 6], "INK", 3),
    rect([46, 39, 12, 6], "INK", 3),
    rect([26, 30, 12, 24], "INK", 4),
    rect([30, 30, 4, 24], "STEEL", 1),
    circle([32, 42, 2.5], "STEEL"),
  ].join(""),

  medicine_ball: [
    shadow(17),
    circle([32, 39, 15], "ACCENT"),
    `<path d="M32 24 C24 31 24 47 32 54 M32 24 C40 31 40 47 32 54 M17 39 H47" fill="none" stroke="INK" stroke-opacity="0.22" stroke-width="1.5"/>`,
  ].join(""),

  exercise_ball: [
    shadow(21),
    circle([32, 34, 20], "STEEL"),
    `<path d="M45 21 A20 20 0 0 1 32 54 A20 20 0 0 0 45 21 Z" fill="INK" fill-opacity="0.14"/>`,
    `<path d="M20 24 A14 14 0 0 1 28 18" fill="none" stroke="INK" stroke-opacity="0.2" stroke-width="2.5" stroke-linecap="round"/>`,
  ].join(""),

  treadmill: [
    shadow(26),
    rect([6, 45, 44, 6], "INK", 3),
    rect([10, 47, 36, 2], "STEEL", 1),
    line("M46 46 L51 19", "STEEL", 3),
    line("M50 26 L36 28", "STEEL", 2.5),
    line("M48 50 V54 M9 50 V54", "STEEL", 2.5),
    `<path d="M42.3 12.8 L58.6 10.1 L60.4 18.6 L44.1 21.4 Z" fill="INK"/>`,
    `<path d="M47 15 L55.5 13.6 L56.2 16.8 L47.7 18.2 Z" fill="ACCENT"/>`,
  ].join(""),

  stationary_bike: [
    shadow(25),
    line("M8 52.5 H56", "STEEL", 3),
    line("M18 52.5 L25 23", "STEEL", 3),
    line("M24 36 L44 39", "STEEL", 3),
    line("M44 52.5 L48 20", "STEEL", 3),
    circle([43, 40, 10], "ACCENT"),
    circle([43, 40, 2.5], "INK"),
    rect([17, 19, 14, 5], "INK", 2.5),
    line("M43 19 H55", "INK", 3.5),
    line("M31 41 L28 47", "INK", 2.5),
    rect([25, 46, 6, 2.5], "INK", 1),
  ].join(""),

  rowing_machine: [
    shadow(27),
    line("M6 46 H46", "STEEL", 3),
    line("M8 46 V53 M44 46 V53", "STEEL", 2.5),
    circle([50, 41, 9], "ACCENT"),
    circle([50, 41, 2.5], "INK"),
    rect([14, 40, 13, 5], "INK", 2.5),
    line("M36 45 L39.5 35", "INK", 4),
    line("M41 40 L47.5 40", "INK", 1),
    line("M37 37 V43", "INK", 3),
    line("M46 50 L54 50 V53", "STEEL", 2.5),
  ].join(""),

  elliptical: [
    shadow(26),
    line("M6 52.5 H58", "STEEL", 3),
    circle([14, 43, 8], "ACCENT"),
    circle([14, 43, 2.5], "INK"),
    line("M14 43 L44 40", "STEEL", 2.5),
    line("M44 52.5 L46 16", "STEEL", 3),
    line("M38 14 L33 34", "STEEL", 2.5),
    line("M54 14 L50 34", "STEEL", 2.5),
    rect([21, 38, 11, 3.5], "INK", 1.75),
    rect([40, 9, 12, 7], "INK", 2),
  ].join(""),

  stair_climber: [
    shadow(26),
    `<path d="M8 54 V46 H17 V38 H26 V30 H35 V54 Z" fill="INK"/>`,
    line("M8 54 H44", "STEEL", 2.5),
    line("M35 54 V22", "STEEL", 3),
    line("M44 54 L47 14", "STEEL", 3),
    line("M47 22 L31 20", "STEEL", 2.5),
    rect([42, 8, 14, 8], "INK", 2),
    rect([45, 10.5, 8, 3], "ACCENT", 1),
  ].join(""),
};

/** A gym's own machine has no drawing of its own; it borrows the most typical piece of its category. */
const CATEGORY_ART: Record<EquipmentCategory, EquipmentKey> = {
  [EquipmentCategory.FREE_WEIGHTS]: "dumbbells",
  [EquipmentCategory.BARS_AND_PLATES]: "barbell",
  [EquipmentCategory.BENCHES_AND_RACKS]: "flat_bench",
  [EquipmentCategory.CABLES]: "cable_station",
  [EquipmentCategory.PLATED_MACHINES]: "leg_press",
  [EquipmentCategory.WEIGHT_MACHINES]: "chest_press_machine",
  [EquipmentCategory.BANDS_AND_ACCESSORIES]: "resistance_bands",
  [EquipmentCategory.CARDIO]: "treadmill",
};

export type EquipmentArtPaints = { ink: string; steel: string; accent: string };

/**
 * The item's illustration as a standalone SVG document, painted with any CSS color - a resolved
 * `hsl(...)` on native, a `var(--token)` on the web, so the drawing follows the theme. Paints go
 * into inline `style`, which both browsers and react-native-svg's `SvgXml` read.
 */
export const equipmentArtSvg = (
  { equipmentKey, category }: { equipmentKey: string; category?: string | null },
  paints: EquipmentArtPaints,
) => {
  const ownCategory = Object.values(EquipmentCategory).find((candidate) => candidate === category);
  const key = isEquipmentKey(equipmentKey)
    ? equipmentKey
    : CATEGORY_ART[ownCategory ?? EquipmentCategory.WEIGHT_MACHINES];
  const byToken = { INK: paints.ink, STEEL: paints.steel, ACCENT: paints.accent };
  const body = EQUIPMENT_ART[key].replace(
    /(fill|stroke)="(INK|STEEL|ACCENT)"/g,
    (_, property: string, token: keyof typeof byToken) => `style="${property}:${byToken[token]}"`,
  );
  return `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 64 64">${body}</svg>`;
};
